<script>
// Exhibitor-side recommendations: visitors whose interests match the company
// lead profile (wiki §29, 6.5). Without companyId it lists one row per company
// the user is an accepted exhibitor member of (same eligibility as the
// Aussteller-Postfach); with companyId it shows the recommended visitors with
// the shared connect CTA. Relative imports only -- also bundled into
// speaker.bundle for the CMS plugin.
import RecCard from './rec-card.vue'

export default {
  name: 'ExhibitorVisitorRecommendations',
  components: { RecCard },
  props: {
    companyId: { type: String, default: '' },
    // Optional event short name; backend defaults to all active events.
    eventShortName: { type: String, default: '' },
  },

  data() {
    return {
      companies: [],
      companiesLoaded: false,
      visitors: [],
      loaded: false,
      // '' = all fairs of the company; otherwise one event short name.
      selectedEvent: '',
    }
  },

  computed: {
    company() {
      return this.companies.find(
        (c) => String(c.company_id) === String(this.companyId))
    },
    // "ZPE26, ZPS26 - ACME GmbH" -- Exhibitor.__str__ format.
    companyName() {
      return this.company
        ? this.withEvents(this.company.name, this.company.event_short_names) : ''
    },
    // Fair filter pills only make sense with more than one fair.
    companyEvents() {
      return (this.company && this.company.event_short_names) || []
    },
  },

  watch: {
    companyId() { this.selectedEvent = ''; this.loadVisitors() },
    selectedEvent() { this.loadVisitors() },
  },

  created() {
    this.loadCompanies()
    if (this.companyId) this.loadVisitors()
  },

  methods: {
    // Same one-shot retry as recommendation-index: in the CMS embed the token
    // bootstrap (webapp-config readToken) is fire-and-forget, so the very
    // first request can arrive before the token is set.
    retryOnce(error, retryFn) {
      const status = error && error.response && error.response.status
      if (status === 401 || status === 403) {
        if (this.$storage && this.$storage.fetchUser) this.$storage.fetchUser()
        setTimeout(retryFn, 600)
        return true
      }
      return false
    },

    loadCompanies(retry = true) {
      let me = this
      this.$chat.get('exhibitor-inbox/').then(({ data }) => {
        me.companies = data || []
        me.companiesLoaded = true
        // The company picker only matters for multi-company crew members --
        // with exactly one company jump straight to its recommendations
        // (replace, so the back button skips the empty picker).
        if (!me.companyId && me.companies.length === 1) {
          me.openCompany(me.companies[0], true)
        }
      }).catch((error) => {
        if (!retry || !me.retryOnce(error, () => me.loadCompanies(false))) {
          me.companiesLoaded = true
        }
      })
    },

    loadVisitors(retry = true) {
      if (!this.companyId) return
      let me = this
      this.loaded = false
      const params = { company_id: this.companyId, limit: 10 }
      const event = this.selectedEvent || this.eventShortName
      if (event) params.event = event
      this.$api.get('exhibitor/recommended-visitors/', { params }).then((response) => {
        me.visitors = (response.data && response.data.visitors) || []
        me.loaded = true
      }).catch((error) => {
        if (!retry || !me.retryOnce(error, () => me.loadVisitors(false))) {
          me.loaded = true
        }
      })
    },

    // Exhibitor.__str__ format: "ZPE26, ZPS26 - ACME GmbH".
    withEvents(name, shortNames) {
      return (shortNames && shortNames.length)
        ? `${shortNames.join(', ')} - ${name}` : name
    },

    openCompany(company, replace = false) {
      // Works under the SPA route (/exhibitor-recommendations) and the CMS
      // plugin router (base route '/'): append the id to the current path.
      const path = this.$route.path === '/'
        ? `/${company.company_id}`
        : `${this.$route.path.replace(/\/$/, '')}/${company.company_id}`
      const nav = replace ? this.$router.replace(path) : this.$router.push(path)
      nav.catch(() => {})
    },
  },
}
</script>

<template>
  <div>

    <!-- Overview: one row per company -->
    <template v-if="!companyId">
      <div v-if="!companiesLoaded" class="text-center text-muted py-4">
        <i class="fas fa-spinner fa-spin"></i>
      </div>
      <div v-else-if="companies.length === 0" class="text-muted text-center mt-4">
        Du bist kein Mitglied eines Aussteller-Teams.
      </div>
      <div v-else class="list-group mt-1">
        <a v-for="c in companies" :key="c.company_id" href="#"
         class="text-decoration-none" @click.prevent="openCompany(c)">
          <div class="list-group-item list-group-item-action d-flex align-items-center">
            <img v-if="c.logo" :src="c.logo" class="c-logo me-2" />
            <div class="flex-fill">
              <div class="fw-bold">{{ withEvents(c.name, c.event_short_names) }}</div>
            </div>
            <i class="fas fa-chevron-right text-muted"></i>
          </div>
        </a>
      </div>
    </template>

    <!-- Recommended visitors of one company -->
    <template v-else>
      <h3 v-if="companyName" class="h5 mb-2">
        <i class="bi bi-stars text-primary me-1"></i>
        Empfohlene Kontakte für {{ companyName }}
      </h3>

      <ul v-if="companyEvents.length > 1" class="nav nav-pills mb-3 gap-1">
        <li class="nav-item"><a href="#" class="nav-link" :class="{active: selectedEvent === ''}"
          @click.prevent="selectedEvent = ''">Alle</a></li>
        <li v-for="sn in companyEvents" :key="sn" class="nav-item">
          <a href="#" class="nav-link" :class="{active: selectedEvent === sn}"
           @click.prevent="selectedEvent = sn">{{ sn }}</a>
        </li>
      </ul>

      <div v-if="!loaded" class="text-center text-muted py-4">
        <i class="fas fa-spinner fa-spin"></i>
      </div>
      <div v-else-if="visitors.length === 0" class="text-muted text-center mt-4">
        <p class="mb-1">Keine passenden Besucher gefunden.</p>
        <p class="mb-0"><small>
          Tipp: Pflegt eure Kategorien im Unternehmensprofil,
          um passendere Empfehlungen zu erhalten.
        </small></p>
      </div>
      <div v-else class="list-group">
        <rec-card v-for="v in visitors" :key="v.uuid" :item="v" />
      </div>
    </template>

  </div>
</template>

<style scoped>
.c-logo {
  height: 36px;
  width: 36px;
  object-fit: contain;
}
</style>
